/**
 * Portaled column-header context menu for the attribute table.
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Menu, tokens, type MenuItemDef } from '@/design';

export type AttributeHeaderAction =
  | 'sort-asc'
  | 'sort-desc'
  | 'custom-sort'
  | 'fields'
  | 'hide'
  | 'freeze'
  | 'calculate-field'
  | 'calculate-toolbar'
  | 'calculate-geometry'
  | 'explore-stats'
  | 'visualize-stats'
  | 'summarize';

export interface AttributeHeaderMenuProps {
  readonly column: string;
  readonly x: number;
  readonly y: number;
  readonly frozen: boolean;
  readonly canHide: boolean;
  readonly hasGeometry: boolean;
  readonly onAction: (action: AttributeHeaderAction, column: string) => void;
  readonly onClose: () => void;
  readonly 'data-testid'?: string;
}

export function buildHeaderMenuItems(options: {
  readonly frozen: boolean;
  readonly canHide: boolean;
  readonly hasGeometry: boolean;
}): MenuItemDef[] {
  return [
    { id: 'sort-asc', label: 'Sort Ascending', icon: 'sort' },
    { id: 'sort-desc', label: 'Sort Descending', icon: 'swap_vert' },
    { id: 'custom-sort', label: 'Custom Sort', icon: 'tune', shortcut: 'Ctrl+Shift+S' },
    { id: 'sep-fields', label: '', separator: true },
    { id: 'fields', label: 'Fields', icon: 'view_column' },
    {
      id: 'hide',
      label: 'Hide Field',
      icon: 'visibility_off',
      disabled: !options.canHide,
      disabledReason: 'At least one column must stay visible.',
    },
    {
      id: 'freeze',
      label: options.frozen ? 'Unfreeze Field' : 'Freeze Field',
      icon: 'push_pin',
    },
    { id: 'sep-calc', label: '', separator: true },
    { id: 'calculate-field', label: 'Calculate Field', icon: 'function' },
    {
      id: 'calculate-toolbar',
      label: 'Calculate Field Toolbar',
      icon: 'edit_note',
      shortcut: 'Ctrl+Shift+F',
    },
    {
      id: 'calculate-geometry',
      label: 'Calculate Geometry',
      icon: 'square_foot',
      disabled: !options.hasGeometry,
      disabledReason: 'This layer has no geometry to calculate from.',
    },
    { id: 'sep-stats', label: '', separator: true },
    { id: 'explore-stats', label: 'Explore Statistics', icon: 'query_stats' },
    { id: 'visualize-stats', label: 'Visualize Statistics', icon: 'monitoring' },
    { id: 'summarize', label: 'Summarize', icon: 'summarize' },
  ];
}

export function AttributeHeaderMenu({
  column,
  x,
  y,
  frozen,
  canHide,
  hasGeometry,
  onAction,
  onClose,
  'data-testid': testId = 'attribute-header-menu',
}: AttributeHeaderMenuProps) {
  const items = buildHeaderMenuItems({ frozen, canHide, hasGeometry });

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest(`[data-testid="${testId}"]`)) return;
      onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, testId]);

  const menu = (
    <Menu
      aria-label={`Column actions for ${column}`}
      items={items}
      onSelect={(id) => {
        onAction(id as AttributeHeaderAction, column);
        onClose();
      }}
      onClose={onClose}
      data-testid={testId}
      style={{
        position: 'fixed',
        left: x,
        top: y,
        minWidth: tokens.layout.popoverXs,
        padding: tokens.space[1],
        borderRadius: tokens.radius.md,
        border: tokens.border.thin,
        background: tokens.color.surface[1],
        boxShadow: tokens.elevation[2],
        zIndex: tokens.zIndex.toast,
      }}
    />
  );

  if (typeof document === 'undefined') return menu;
  return createPortal(menu, document.body);
}
